import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  assignBarMarks,
  createEstimateDefaults,
  createJob,
  deserializeJob,
  generateWeightSummary,
  rollupSegments,
  serializeJob,
  type BarMark,
  type EstimateDefaults,
  type Job,
  type Segment,
  type WeightSummaryEntry,
} from '@otto/rebar-engine';
import { useAuth } from '../lib/auth.tsx';
import { getSubmission, getTakeoffSession, upsertTakeoffSession } from '../lib/db.ts';
import type { Submission, TakeoffSessionRow } from '../lib/types.ts';
import { Placeholder } from '../components/Placeholder.tsx';
import { SegmentTree } from '../components/takeoff-rebar/SegmentTree';
import { cloneSegs } from '../components/takeoff-rebar/helpers';
import { COLORS, mono } from '../components/takeoff-rebar/styles';
import {
  Card,
  ErrorBox,
  Loader,
  MetaRow,
  SaveBadge,
  type SaveStatus,
} from '../components/takeoff/primitives.tsx';

function serializeWeightEntry(entry: WeightSummaryEntry) {
  return {
    size: entry.size.toString(),
    total: entry.total,
    straight: entry.straight,
    heavy: entry.heavy,
    light: entry.light,
    stockWeight: entry.stockWeight,
    barCount: entry.barCount,
    totalLinealFeet: entry.totalLinealFeet,
    tons: entry.tons,
  };
}

export function TakeoffRebar() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <NoSubmissionSelected />;
  return <TakeoffRebarWorkspace submissionId={id} />;
}

function NoSubmissionSelected() {
  return (
    <Placeholder
      increment="Increment 4 — Structural takeoff"
      title="Takeoff · Rebar"
      blurb="Open a job from the Jobs list to start a structural takeoff. The session persists across reloads and computes weights live via @otto/rebar-engine."
    />
  );
}

function TakeoffRebarWorkspace({ submissionId }: { submissionId: string }) {
  const { user } = useAuth();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [sessionRow, setSessionRow] = useState<TakeoffSessionRow | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [defaults] = useState<EstimateDefaults>(() => createEstimateDefaults());
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Load submission + takeoff session (or create empty job)
  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const sub = await getSubmission(submissionId);
        if (!active) return;
        if (!sub) {
          setError('Submission not found.');
          return;
        }
        setSubmission(sub);

        const existing = await getTakeoffSession(submissionId, 'structural');
        if (!active) return;

        if (existing && existing.job_data && Object.keys(existing.job_data).length > 0) {
          const restored = deserializeJob(JSON.stringify(existing.job_data));
          setJob(restored);
          setSessionRow(existing);
        } else {
          const fresh = createJob({
            jobId: submissionId,
            projectName: sub.job_name,
            source: 'manual_entry',
          });
          setJob(fresh);
          setSessionRow(existing);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Load failed');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [submissionId]);

  // Live rollup + weight summary
  const { weightSummary, totalTons, totalLbs } = useMemo(() => {
    if (!job) return { weightSummary: [] as WeightSummaryEntry[], totalTons: 0, totalLbs: 0 };
    const cloned: Job = { ...job, segments: cloneSegs(job.segments) };
    const withMarks = assignBarMarks(cloned);
    const barMarks = new Map<string, BarMark>(withMarks.barMarks.map((m) => [m.barMarkId, m]));
    rollupSegments(withMarks.segments, barMarks, defaults);
    const allLines = withMarks.segments.flatMap((s) => s.lines);
    const summary = generateWeightSummary(allLines, defaults);
    const tons = summary.reduce((sum, entry) => sum + entry.tons, 0);
    const lbs = summary.reduce((sum, entry) => sum + entry.total, 0);
    return { weightSummary: summary, totalTons: tons, totalLbs: lbs };
  }, [job, defaults]);

  // Debounced save on every job mutation. We read weightSummary/totalTons via
  // refs at fire time so weight recalculation does not reset the debounce.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userId = user?.id ?? null;
  const weightSummaryRef = useRef(weightSummary);
  const totalTonsRef = useRef(totalTons);
  useEffect(() => {
    weightSummaryRef.current = weightSummary;
    totalTonsRef.current = totalTons;
  }, [weightSummary, totalTons]);
  useEffect(() => {
    if (!job || !userId || loading) return;
    setSaveStatus('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const jobJson = JSON.parse(serializeJob(job)) as Record<string, unknown>;
        const summarySnapshot = weightSummaryRef.current;
        const tonsSnapshot = totalTonsRef.current;
        const summaryJson =
          summarySnapshot.length > 0
            ? { entries: summarySnapshot.map(serializeWeightEntry), totalTons: tonsSnapshot }
            : null;
        const row = await upsertTakeoffSession({
          submissionId,
          userId,
          discipline: 'structural',
          jobData: jobJson,
          weightSummary: summaryJson,
          status: job.segments.length > 0 ? 'in_progress' : 'draft',
        });
        setSessionRow(row);
        setSaveStatus('saved');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Save failed');
        setSaveStatus('error');
      }
    }, 600);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [job, userId, submissionId, loading]);

  const onSegmentsChange = useCallback(
    (segments: Segment[]) => {
      setJob((curr) => (curr ? { ...curr, segments } : curr));
    },
    [],
  );

  if (loading) return <Loader text="Loading takeoff…" />;
  if (error) return <ErrorBox message={error} />;
  if (!submission || !job) return <Loader text="Loading…" />;

  const totalLines = job.segments.reduce((n, s) => n + s.lines.length, 0);

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Link to={`/app/jobs/${submissionId}`} style={{ ...mono(12, COLORS.textDim), textDecoration: 'none' }}>
          ← {submission.job_name}
        </Link>
        <div style={{ flex: 1 }} />
        <SaveBadge status={saveStatus} />
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="SEGMENTS" value={String(job.segments.length)} />
        <Stat label="LINE ITEMS" value={String(totalLines)} />
        <Stat
          label="TOTAL WEIGHT"
          value={totalLbs > 0 ? `${Math.round(totalLbs).toLocaleString()} lb` : '—'}
          accent={totalLbs > 0 ? COLORS.green : COLORS.textDim}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>
        <SegmentTree job={job} onChange={onSegmentsChange} />

        {/* Right sidebar: weight summary + session */}
        <div style={{ position: 'sticky', top: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
            <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>
              WEIGHT SUMMARY
            </div>
            {weightSummary.length === 0 ? (
              <div style={{ fontSize: 12, color: COLORS.textDim }}>No lines entered.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr 1fr',
                    gap: 8,
                    ...mono(10, COLORS.textDim),
                    marginBottom: 4,
                  }}
                >
                  <div>SIZE</div>
                  <div style={{ textAlign: 'right' }}>LBS</div>
                  <div style={{ textAlign: 'right' }}>TONS</div>
                </div>
                {weightSummary.map((entry) => (
                  <div
                    key={entry.size.toString()}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '60px 1fr 1fr',
                      gap: 8,
                      fontSize: 13,
                      color: COLORS.textMuted,
                    }}
                  >
                    <div style={mono(13, COLORS.text)}>{entry.size.toString()}</div>
                    <div style={{ textAlign: 'right' }}>{Math.round(entry.total).toLocaleString()}</div>
                    <div style={{ textAlign: 'right' }}>{entry.tons.toFixed(2)}</div>
                  </div>
                ))}
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: `1px solid ${COLORS.border}`,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    fontSize: 13,
                  }}
                >
                  <span style={mono(11, COLORS.textDim)}>TOTAL</span>
                  <span style={mono(13, COLORS.green)}>{totalTons.toFixed(2)} tons</span>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 8 }}>SESSION</div>
            <MetaRow label="DISCIPLINE" value="structural" />
            <MetaRow label="STATUS" value={sessionRow?.status ?? 'draft'} />
            <MetaRow label="SEGMENTS" value={String(job.segments.length)} />
            <MetaRow label="LINES" value={String(totalLines)} />
          </Card>
        </div>
      </div>
    </div>
  );
}

// --- Rebar-specific primitives ---

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Card>
      <div style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          fontSize: 24,
          color: accent ?? COLORS.text,
        }}
      >
        {value}
      </div>
    </Card>
  );
}
